import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from "@angular/core";
import {
  ProjectHubBoardListItem,
  ProjectHubSpark,
  tagColor,
} from "../../project-hub.types";
import { TimeAgoPipe } from "src/app/pipes/time-ago.pipe";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { ProjectHubTruncateService } from "../project-hub-content-truncate.service";
import { DxContextMenuModule } from "devextreme-angular";
import { ItemClickEvent } from "devextreme/ui/context_menu";
import { CommonModule } from "@angular/common";
import { ConfigService } from "src/app/services/config.service";
import { ProjectHubService } from "src/app/services/project-hub.service";
import { SparkTagsOverflowComponent } from "../spark-tags-overflow/spark-tags-overflow.component";
import { Subscription } from "rxjs";
import { AppGlobals } from "src/app/app.globals";
import { SparkHistoryService } from "../spark-history-overlay/spark-history.service";

@Component({
    selector: "app-pinned-spark-card",
    templateUrl: "./pinned-spark.component.html",
    styleUrls: ["../spark-card.shared.css", "./pinned-spark.component.css"],
    imports: [
        CommonModule,
        TimeAgoPipe,
        SvgIconComponent,
        DxContextMenuModule,
        SparkTagsOverflowComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PinnedSparkCardComponent {
  
  @ViewChild('lastModifiedDateElem', { static: false })
  lastModifiedDateElem: ElementRef;
  isLastModifiedDateOverflow: boolean = false;
  @Input() spark: ProjectHubSpark;
  @Input() selected: boolean = false;
  @Input() tags: {
    [key: string]: { id: number; name: string; color?: tagColor };
  };
  @Input() currentBoardId!: number;
  @Output() edit = new EventEmitter<ProjectHubSpark>();
  @Output() delete = new EventEmitter<ProjectHubSpark>();
  @Output() pin = new EventEmitter<ProjectHubSpark>();
  @Output() unpin = new EventEmitter<ProjectHubSpark>();
  @Output() move = new EventEmitter<{
    spark: ProjectHubSpark;
    targetBoardId: number;
  }>();
  @Output() onSelect = new EventEmitter<ProjectHubSpark>();

  contextMenuItems = [
    { text: $localize`:@@edit:Edit`, id: "edit" },
    { text: $localize`:@@history:History`, id: "history" },
    { text: $localize`:@@delete:Delete`, id: "delete" },
    { text: $localize`:@@pin:Pin`, id: "pin" },
    { text: $localize`:@@unpin:Unpin`, id: "unpin" },
  ];

  boardSub: Subscription;
  boards: ProjectHubBoardListItem[] = [];

  constructor(
    private truncateService: ProjectHubTruncateService,
    private cdr: ChangeDetectorRef,
    public configService: ConfigService,
    private projectHubService: ProjectHubService,
    private sparkHistoryService: SparkHistoryService
  ) {
    this.boardSub = this.projectHubService.boardsSubject.subscribe(
      (boards) => (this.boards = boards)
    );
  }

  ngAfterViewInit() {
    this.checkLastModifiedDateOverflow();
  }

  ngOnChanges() {
    this.checkLastModifiedDateOverflow();
  }

  checkLastModifiedDateOverflow() {
    if (this.lastModifiedDateElem && this.lastModifiedDateElem.nativeElement) {
      const el = this.lastModifiedDateElem.nativeElement;
      this.isLastModifiedDateOverflow = el.scrollWidth > el.clientWidth;
      this.cdr.detectChanges();
    }
  }

  @ViewChild("pinnedSparkContentContainer", { static: false })
  pinnedSparkContentContainer: ElementRef;
  contextVisible = false;

  get displayContent(): string {
    const truncatedContent = this.truncateService.truncateContent(
      this.pinnedSparkContentContainer,
      this.spark.content.text
    );
    return truncatedContent.content;
  }

  onContextMenuItemClick(event: ItemClickEvent) {
    event.event.stopPropagation();
    const action = (event.itemData as any).id;
    this.executeMenuAction(action);
  }

  onClick() {
    this.onSelect.emit(this.spark);
  }

  onMenuClick(event: Event) {
    event.stopPropagation();
    this.contextVisible = !this.contextVisible;
  }

  private executeMenuAction(actionId: string): void {
    switch (actionId) {
      case "edit":
        this.edit.emit(this.spark);
        break;
      case "history":
        this.viewHistory();
        break;
      case "delete":
        this.delete.emit(this.spark);
        break;
      case "pin":
        this.pin.emit(this.spark);
        break;
      case "unpin":
        this.unpin.emit(this.spark);
        break;
      default:
        // Handle move to board actions (board IDs are passed as action IDs)
        if (actionId.startsWith("move-to-")) {
          const targetBoardId = parseInt(actionId.replace("move-to-", ""));
          this.move.emit({ spark: this.spark, targetBoardId });
        }
        break;
    }
  }

  private viewHistory(): void {
    if (!this.spark.id) return;

    // Open spark history overlay
    this.sparkHistoryService.openSparkHistory(this.spark.id);
  }

  get visibleMenuItems(): any[] {
    const baseItems = this.contextMenuItems.filter((item) => {
      if (item.id === "pin" && this.spark.pinned) return false;
      if (item.id === "unpin" && !this.spark.pinned) return false;
      return true;
    });

    if (this.boards.length > 1) {
      const moveToItem = {
        text: $localize`:@@moveTo:Move to`,
        id: "move-to",
        items: this.boards
          .filter((board) => board.id !== this.currentBoardId)
          .map((board) => ({
            text: board.name,
            id: `move-to-${board.id}`,
          })),
      };

      // Insert "Move to" item before the last item (typically Delete)
      const insertIndex = baseItems.length > 0 ? baseItems.length - 1 : 0;
      baseItems.splice(insertIndex, 0, moveToItem);
    }

    return baseItems;
  }

  /**
   * Returns the translated tooltip for the author date span.
   */
  getLastEditedTooltip(): string {
    const created = this.spark?.dateCreated ? new TimeAgoPipe().transform(this.spark.dateCreated) : '';
    if (this.spark?.lastModifiedDate) {
      const lastEdited = new TimeAgoPipe().transform(this.spark.lastModifiedDate);
      const lastModifiedBy = (this.configService.userMap$.value?.[this.spark.lastModifiedBy] || this.spark.lastModifiedBy);
      return $localize`:@@lastEditedTooltip:Created ${created}, last edited ${lastEdited} by ${lastModifiedBy}`;
    }
  }
}
